import { useEffect, useRef } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { find } from "lodash";

import { FormValidationMethods } from "@/components/Form/types";
import { getErrorMessage, getStatus } from "../../Input/adapter/utils";

interface UseSelectAdapterOptions {
  name: string;
  defaultValue?: string;
  value?: string;
  error?: Record<string, unknown>;
  errorMessage?: any;
  success?: boolean;
  loading?: boolean;
  disabled?: boolean;
  onChange?: (value: string) => void;
  onClick?: (value: string) => void;
  rules?: any;
}

export const useSelectAdapter = ({
  name,
  defaultValue,
  value: externalValue,
  error,
  errorMessage,
  success,
  loading,
  disabled,
  onChange,
  onClick,
  rules,
}: UseSelectAdapterOptions) => {
  const methods: FormValidationMethods = useFormContext();
  const registeredRef = useRef(false);

  const watchedValue = useWatch({
    name,
    control: methods?.control,
  });

  // Register field with react-hook-form
  useEffect(() => {
    if (!methods || !name) return;

    const schemaRules = methods.schema && find(methods.schema, { name });

    const finalRules: any = {
      ...(schemaRules?.rules || {}),
      ...(rules || {}),
    };

    methods.register(name, finalRules);
    registeredRef.current = true;
  }, [name, methods, rules]);

  useEffect(() => {
    if ((defaultValue !== undefined || externalValue !== undefined) && registeredRef.current) {
      methods.setValue(name, defaultValue || externalValue);
    }
  }, []);

  const handleChange = (value: string) => {
    if (methods?.formState.errors[name]) {
      methods.clearErrors(name);
    }

    methods?.setValue(name, value);

    if (onClick) {
      onClick(value);
    }
    if (onChange) {
      onChange(value);
    }
  };

  const currentValue =
    externalValue !== undefined ? externalValue : (watchedValue ?? defaultValue ?? "");

  const status = getStatus(error, errorMessage, success);

  const currentErrorMessage = errorMessage || methods?.formState?.errors?.[name];
  const finalErrorMessage: React.ReactNode = currentErrorMessage
    ? getErrorMessage(currentErrorMessage)
    : undefined;

  const isDisabled = disabled || loading;

  return {
    currentValue,
    status,
    finalErrorMessage,
    isDisabled,
    handleChange,
  };
};
